import { useState } from 'react';
import { api, ApiError } from '../../api-client.js';

// ---------------------------------------------------------------------------
// Private lobby creator
// ---------------------------------------------------------------------------
// Everything the brief lets a lobby creator set. The public queue does
// NOT use this form — queue lobbies run on defaults and cannot be
// configured, which is what keeps the queue a single comparable pool.
//
// Every option here is also whitelisted and clamped server-side; this
// form is convenience, never the boundary.

type Options<T extends string> = ReadonlyArray<{ value: T; label: string; hint?: string }>;

/** The default reserve, mirrored from the engine's GB_BASE_RESERVE. */
const DEFAULT_RESERVE = [
  10, 50, 100, 250, 500, 750, 1000, 1500, 2500, 3500, 5000, 7500, 10000,
  15000, 20000, 25000, 35000, 50000, 75000, 100000
];

const RESERVE_MIN_ENTRIES = 4;
const RESERVE_MAX_ENTRIES = 40;
const RESERVE_MAX_VALUE = 10_000_000;

const ANONYMITY: Options<'hidden' | 'lateReveal' | 'openBook' | 'tiered'> = [
  { value: 'lateReveal', label: 'Late reveal', hint: 'Finalists see each other' },
  { value: 'hidden', label: 'Hidden', hint: 'Nobody until the end' },
  { value: 'openBook', label: 'Open book', hint: 'Records from round one' },
  { value: 'tiered', label: 'Tiered', hint: 'Anonymous, matched by record' }
];

const TIE_BREAKS: Options<'potDamage' | 'killersFirst' | 'cashFirst'> = [
  { value: 'potDamage', label: 'Pot damage', hint: 'Cash ÷ 10^killers' },
  { value: 'killersFirst', label: 'Killers first' },
  { value: 'cashFirst', label: 'Cash first' }
];

const PERSONALITIES: Options<'veiled' | 'classic' | 'wild'> = [
  { value: 'veiled', label: 'Veiled', hint: 'Never disclosed' },
  { value: 'classic', label: 'Classic' },
  { value: 'wild', label: 'Wild' }
];

const INFLATION: Options<'reserve' | 'augmented' | 'inflationOnly'> = [
  { value: 'reserve', label: 'Reserve' },
  { value: 'augmented', label: 'Augmented' },
  { value: 'inflationOnly', label: 'Inflation only' }
];

export function LobbyCreator({
  userId,
  onCreated,
  onCancel
}: {
  userId: string;
  onCreated: (matchId: string) => void;
  onCancel?: () => void;
}) {
  // STING on by default — matches the engine default. The creator used
  // to send 'twoBall' explicitly, which silently overrode it.
  const [stingBall, setStingBall] = useState(true);
  const [anonymityMode, setAnonymityMode] =
    useState<'hidden' | 'lateReveal' | 'openBook' | 'tiered'>('lateReveal');
  const [tieBreakPreset, setTieBreakPreset] =
    useState<'potDamage' | 'killersFirst' | 'cashFirst'>('potDamage');
  const [botPersonalityMode, setBotPersonalityMode] =
    useState<'veiled' | 'classic' | 'wild'>('veiled');
  const [inflationSetting, setInflationSetting] =
    useState<'reserve' | 'augmented' | 'inflationOnly'>('reserve');
  // Fill the table by default. At 0 the creator was seated alone and the
  // match sat at "1 of 4" until they found the separate "start now with
  // bots" button — the game simply never began. Turn it down to invite
  // humans; the picker is right there.
  const [botCount, setBotCount] = useState(3);
  const [botsRevealed, setBotsRevealed] = useState(false);
  const [freeChatEnabled, setFreeChatEnabled] = useState(false);
  const [reserve, setReserve] = useState<number[]>(DEFAULT_RESERVE);
  const [newValue, setNewValue] = useState('');
  const [r1PerSeat, setR1PerSeat] = useState(4);
  const [r1Front, setR1Front] = useState(2);
  const [r2PerSeat, setR2PerSeat] = useState(5);
  const [r2Front, setR2Front] = useState(2);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  function addValue() {
    const parsed = Number(newValue.replace(/[^0-9]/g, ''));
    if (!Number.isFinite(parsed) || parsed < 1 || parsed > RESERVE_MAX_VALUE) return;
    if (reserve.length >= RESERVE_MAX_ENTRIES || reserve.includes(parsed)) {
      setNewValue('');
      return;
    }
    setReserve([...reserve, parsed].sort((a, b) => a - b));
    setNewValue('');
  }

  async function create() {
    setBusy(true);
    setError(null);
    try {
      const created = await api.createConfiguredMatch({
        gameKey: 'golden-balls',
        createdByUserId: userId,
        goldenBalls: {
          ballMode: stingBall ? 'threeBall' : 'twoBall',
          anonymityMode,
          tieBreakPreset,
          botPersonalityMode,
          inflationSetting,
          botsRevealed,
          freeChatEnabled,
          reserveValues: reserve,
          handSizes: {
            roundOne: { perSeat: r1PerSeat, front: r1Front },
            roundTwo: { perSeat: r2PerSeat, front: r2Front }
          },
          isPrivateLobby: true
        }
      });
      const matchId = created.match.id;
      await api.joinMatch(matchId, userId);
      // Seat exactly the requested number of bots; the rest stay open
      // for humans joining by link.
      if (botCount > 0) {
        await api.fillBotSeats(matchId, userId, botCount);
      }
      onCreated(matchId);
    } catch (error) {
      setError(error instanceof ApiError ? error.message : String(error));
    } finally {
      setBusy(false);
    }
  }

  return (
    <section className="flex flex-col gap-3 rounded-lg border border-ec-line bg-ec-panel p-3">
      <header>
        <h3 className="font-display text-sm font-semibold uppercase tracking-wide text-ec-face">
          Private lobby
        </h3>
        <p className="font-serif text-xs text-ec-bone-dim">
          Four seats. Share the match link to fill them. Play money only.
        </p>
      </header>

      <Toggle
        label="STING ball"
        hint="A third ball: call their steal and take the pot instead."
        checked={stingBall}
        onChange={setStingBall}
      />
      <Choice label="Anonymity" options={ANONYMITY} value={anonymityMode} onChange={setAnonymityMode} />
      <Choice label="Tie-break" options={TIE_BREAKS} value={tieBreakPreset} onChange={setTieBreakPreset} />
      <Choice label="Round two values" options={INFLATION} value={inflationSetting} onChange={setInflationSetting} />
      <Choice label="Bot personalities" options={PERSONALITIES} value={botPersonalityMode} onChange={setBotPersonalityMode} />

      <div className="flex flex-col gap-1">
        <span className="font-display text-[0.6rem] uppercase tracking-wide text-ec-bone-dim">
          Bots ({botCount} of 3 seats)
        </span>
        <div className="flex gap-1">
          {[0, 1, 2, 3].map((count) => (
            <button
              key={count}
              type="button"
              onClick={() => setBotCount(count)}
              aria-pressed={botCount === count}
              className={[
                'min-h-[2.75rem] flex-1 rounded border font-display text-xs transition',
                botCount === count
                  ? 'border-ec-brass bg-ec-brass/15 text-ec-brass'
                  : 'border-ec-line text-ec-bone-dim'
              ].join(' ')}
            >
              {count}
            </button>
          ))}
        </div>
      </div>

      {/* THE BALLS. Add, remove or edit any prize amount. Everything
          here is re-checked and clamped server-side. */}
      <div className="flex flex-col gap-1">
        <span className="font-display text-[0.6rem] uppercase tracking-wide text-ec-bone-dim">
          Prize reserve ({reserve.length} values)
          {reserve.length < RESERVE_MIN_ENTRIES && (
            <span className="ml-1 text-ec-oxblood-2">
              — need at least {RESERVE_MIN_ENTRIES}
            </span>
          )}
        </span>
        <div className="flex max-h-32 flex-wrap gap-1 overflow-y-auto rounded border border-ec-line bg-ec-panel/60 p-1.5">
          {reserve.map((value) => (
            <button
              key={value}
              type="button"
              title={`Remove ${value.toLocaleString('en-US')}`}
              onClick={() => setReserve(reserve.filter((entry) => entry !== value))}
              className="flex min-h-[2rem] items-center gap-1 rounded border border-ec-line px-1.5 font-serif text-[0.7rem] tabular-nums text-ec-face transition hover:border-ec-oxblood-2/70 hover:text-ec-oxblood-2"
            >
              ${value.toLocaleString('en-US')}
              <span aria-hidden="true" className="text-ec-bone-dim">
                ×
              </span>
            </button>
          ))}
          {reserve.length === 0 && (
            <span className="font-serif text-xs text-ec-bone-dim">
              No values — add some, or reset.
            </span>
          )}
        </div>
        <div className="flex gap-1">
          <input
            type="text"
            inputMode="numeric"
            value={newValue}
            placeholder="Add an amount"
            onChange={(event) => setNewValue(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === 'Enter') {
                event.preventDefault();
                addValue();
              }
            }}
            className="min-h-[2.75rem] min-w-0 flex-1 rounded border border-ec-line bg-ec-panel px-2 font-serif text-xs tabular-nums text-ec-face"
          />
          <button
            type="button"
            onClick={addValue}
            disabled={reserve.length >= RESERVE_MAX_ENTRIES}
            className="ec-btn min-h-[2.75rem] px-3 text-xs disabled:opacity-40"
          >
            Add
          </button>
          <button
            type="button"
            onClick={() => setReserve(DEFAULT_RESERVE)}
            className="ec-btn min-h-[2.75rem] px-3 text-xs"
          >
            Reset
          </button>
        </div>
      </div>

      {/* HAND SIZES. Front row is public, the rest is what you declare
          (and lie about), so the back row can never be empty. */}
      <div className="flex flex-col gap-1">
        <span className="font-display text-[0.6rem] uppercase tracking-wide text-ec-bone-dim">
          Balls dealt per seat
        </span>
        <HandRow
          label="Round one"
          seats={4}
          perSeat={r1PerSeat}
          front={r1Front}
          onPerSeat={(value) => {
            setR1PerSeat(value);
            if (r1Front > value - 1) setR1Front(value - 1);
          }}
          onFront={setR1Front}
        />
        <HandRow
          label="Round two"
          seats={3}
          perSeat={r2PerSeat}
          front={r2Front}
          onPerSeat={(value) => {
            setR2PerSeat(value);
            if (r2Front > value - 1) setR2Front(value - 1);
          }}
          onFront={setR2Front}
        />
      </div>

      <Toggle
        label="Show which seats are bots"
        hint="Off keeps bots indistinguishable, including chat timing."
        checked={botsRevealed}
        onChange={setBotsRevealed}
      />
      <Toggle
        label="Free chat"
        hint="Quick chat is always on. Free chat is filtered and reportable."
        checked={freeChatEnabled}
        onChange={setFreeChatEnabled}
      />

      {error && (
        <p role="alert" className="font-serif text-xs text-ec-oxblood-2">
          {error}
        </p>
      )}

      <div className="flex gap-2">
        <button
          type="button"
          disabled={busy || reserve.length < RESERVE_MIN_ENTRIES}
          onClick={create}
          className="ec-btn ec-btn--primary min-h-[3rem] flex-1 disabled:opacity-40"
        >
          {busy ? 'Creating…' : 'Start game'}
        </button>
        {onCancel && (
          <button
            type="button"
            disabled={busy}
            onClick={onCancel}
            className="ec-btn min-h-[3rem] px-4 disabled:opacity-40"
          >
            Cancel
          </button>
        )}
      </div>
    </section>
  );
}

function Choice<T extends string>({
  label,
  options,
  value,
  onChange
}: {
  label: string;
  options: Options<T>;
  value: T;
  onChange: (value: T) => void;
}) {
  return (
    <div className="flex flex-col gap-1">
      <span className="font-display text-[0.6rem] uppercase tracking-wide text-ec-bone-dim">
        {label}
      </span>
      <div className="flex flex-wrap gap-1">
        {options.map((option) => (
          <button
            key={option.value}
            type="button"
            onClick={() => onChange(option.value)}
            aria-pressed={value === option.value}
            title={option.hint}
            className={[
              'min-h-[2.75rem] flex-1 rounded border px-2 font-display text-[0.65rem] uppercase tracking-wide transition',
              value === option.value
                ? 'border-ec-brass bg-ec-brass/15 text-ec-brass'
                : 'border-ec-line text-ec-bone-dim hover:text-ec-face'
            ].join(' ')}
          >
            {option.label}
          </button>
        ))}
      </div>
    </div>
  );
}

/**
 * One round's hand shape. The back row is derived and shown, because
 * it — not the total — is what the player actually declares and lies
 * about, and it can never be empty.
 */
function HandRow({
  label,
  seats,
  perSeat,
  front,
  onPerSeat,
  onFront
}: {
  label: string;
  seats: number;
  perSeat: number;
  front: number;
  onPerSeat: (value: number) => void;
  onFront: (value: number) => void;
}) {
  return (
    <div className="flex flex-wrap items-center gap-x-3 gap-y-1 rounded border border-ec-line bg-ec-panel/60 px-2 py-1.5">
      <span className="font-display text-[0.6rem] uppercase tracking-wide text-ec-face">
        {label}
      </span>
      <Stepper label="total" value={perSeat} min={2} max={8} onChange={onPerSeat} />
      <Stepper label="face up" value={front} min={1} max={perSeat - 1} onChange={onFront} />
      <span className="font-serif text-[0.65rem] text-ec-bone-dim">
        {perSeat - front} hidden · {seats * perSeat} balls in play
      </span>
    </div>
  );
}

function Stepper({
  label,
  value,
  min,
  max,
  onChange
}: {
  label: string;
  value: number;
  min: number;
  max: number;
  onChange: (value: number) => void;
}) {
  return (
    <span className="flex items-center gap-1">
      <span className="font-display text-[0.55rem] uppercase tracking-wide text-ec-bone-dim">
        {label}
      </span>
      <button
        type="button"
        aria-label={`decrease ${label}`}
        disabled={value <= min}
        onClick={() => onChange(value - 1)}
        className="h-8 w-8 shrink-0 rounded border border-ec-line font-display text-sm text-ec-face disabled:opacity-30"
      >
        −
      </button>
      <span className="w-4 text-center font-serif text-sm tabular-nums text-ec-face">{value}</span>
      <button
        type="button"
        aria-label={`increase ${label}`}
        disabled={value >= max}
        onClick={() => onChange(value + 1)}
        className="h-8 w-8 shrink-0 rounded border border-ec-line font-display text-sm text-ec-face disabled:opacity-30"
      >
        +
      </button>
    </span>
  );
}

function Toggle({
  label,
  hint,
  checked,
  onChange
}: {
  label: string;
  hint?: string;
  checked: boolean;
  onChange: (value: boolean) => void;
}) {
  return (
    <label className="flex min-h-[2.75rem] items-center gap-2">
      <input
        type="checkbox"
        checked={checked}
        onChange={(event) => onChange(event.target.checked)}
        className="h-5 w-5 shrink-0 accent-[rgb(var(--ec-brass-rgb))]"
      />
      <span className="flex flex-col">
        <span className="font-display text-xs uppercase tracking-wide text-ec-face">{label}</span>
        {hint && <span className="font-serif text-[0.65rem] text-ec-bone-dim">{hint}</span>}
      </span>
    </label>
  );
}
